import { useOrderReadiness } from "pages/CreateOrder/hooks/useOrderReadiness";
import { ReactNode, useEffect, useRef } from "react";
import styled from "styled-components";

/** Marks the body while a page-level action bar is mounted. */
const BODY_CLASS = "page-has-sticky-footer";
const HEIGHT_VAR = "--page-sticky-footer-height";

/**
 * The page's action bar, pinned to the bottom of the viewport while the form
 * scrolls behind it.
 *
 * It bleeds through the content container's horizontal gutters so the bar spans
 * the full content width, but deliberately not through the 96px sidebar
 * clearance — the bar starts where the content does. The negative bottom margin
 * cancels the container's own bottom padding so the bar sits flush.
 */
const FooterBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;

  margin-left: calc(-1 * var(--content-gutter));
  margin-right: calc(-1 * var(--content-gutter));
  margin-bottom: calc(-1 * var(--content-gap-bottom));
  padding: 1rem var(--content-gutter);

  background: var(--brand-white);
  border-top: 1px solid var(--brand-gray-200);

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  .footer-hint {
    color: var(--brand-gray-600);
  }
`;

export function StickyFooter({ children }: { children: ReactNode }) {
  const barRef = useRef<HTMLDivElement>(null);
  const isReady = useOrderReadiness();

  // The floating "Powered by" badge is fixed to the viewport bottom and would
  // otherwise sit on top of these buttons. Publishing the bar's height lets the
  // badge lift clear of it; the bar grows when a validation message appears
  // under Schedule send, so it is measured rather than assumed.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) {
      return undefined;
    }

    document.body.classList.add(BODY_CLASS);

    const publishHeight = () => {
      document.body.style.setProperty(
        HEIGHT_VAR,
        `${bar.getBoundingClientRect().height}px`
      );
    };

    publishHeight();

    const observer = new ResizeObserver(publishHeight);
    observer.observe(bar);

    return () => {
      observer.disconnect();
      document.body.classList.remove(BODY_CLASS);
      document.body.style.removeProperty(HEIGHT_VAR);
    };
  }, []);

  return (
    <FooterBar ref={barRef} data-testid="pageActionsFooter">
      <span className="footer-hint" data-testid="pageActionsHint">
        {isReady
          ? "Ready to generate and send"
          : "Please, fill in all required fields to create estimate"}
      </span>

      {children}
    </FooterBar>
  );
}
